/**
 * Imperative confirm / alert dialogs (what @mantine/modals used to give the messenger), on the
 * site's Modal and Button atoms. Callers keep the same calls:
 *   openConfirmModal({ title, children, labels, confirmProps, closeOnConfirm, onConfirm, onCancel })
 *   modals.open({ title, children }), modals.closeAll(), showAlert({ title, message })
 */
import { useCallback, useState } from "react";
import { createRoot } from "react-dom/client";
import Modal from "@/common/components/atom/modal";
import Button from "@/common/components/atom/button";
import { showErrorToast } from "./notifications";
import { useNavigate, useNavigationType } from "../router";

const open = new Map();
let seq = 0;
const DIALOG_SHEET = "hami-dialog";

function render(id) {
  const entry = open.get(id);
  if (!entry) return;
  entry.root.render(entry.view(() => close(id)));
}

function close(id) {
  const entry = open.get(id);
  if (!entry) return;
  open.delete(id);
  entry.root.render(null);
  // unmount after the closing animation
  setTimeout(() => {
    entry.root.unmount();
    entry.host.remove();
  }, 400);
}

function mount(view) {
  const id = `hami-modal-${++seq}`;
  const host = document.createElement("div");
  host.setAttribute("data-hami-modal", id);
  document.body.appendChild(host);
  open.set(id, { host, root: createRoot(host), view });
  render(id);
  return id;
}

export const modals = {
  open({ title, children, withCloseButton = true }) {
    return mount((onClose) => (
      <Modal isOpen onClose={onClose} title={title} noHeader={!withCloseButton && !title} name={DIALOG_SHEET}>
        <div dir="rtl">{children}</div>
      </Modal>
    ));
  },
  close: (id) => close(id),
  closeAll: () => [...open.keys()].forEach(close),
};

/** Runs once the dialog's own history entry is gone: a Back taken before that would only pop the dialog. */
function afterDialogHistory(run) {
  const stack = new URLSearchParams(window.location.search).get("sheets") ?? "";
  if (!stack.split(",").some((token) => token.startsWith(DIALOG_SHEET))) return run();
  let timer = null;
  const go = () => {
    window.removeEventListener("popstate", go);
    window.clearTimeout(timer);
    run();
  };
  window.addEventListener("popstate", go);
  timer = window.setTimeout(go, 400);
}

/** `leaving`: onConfirm leaves the page, so the dialog closes first and it runs after that. */
export function openConfirmModal({
  title,
  children,
  labels = { confirm: "تأیید", cancel: "انصراف" },
  confirmProps,
  closeOnConfirm = true,
  leaving = false,
  onConfirm,
  onCancel,
}) {
  const danger = confirmProps?.color === "red";
  return mount((onClose) => (
    <Modal
      isOpen
      title={title}
      name={DIALOG_SHEET}
      onClose={() => {
        onCancel?.();
        onClose();
      }}
    >
      <div dir="rtl" className="flex flex-col gap-4 pb-2">
        <div className="text-sm leading-7 text-slate-700">{children}</div>
        <div className="flex gap-2">
          <Button
            block
            theme={danger ? "error" : "simple"}
            onClick={async () => {
              if (leaving) {
                onClose();
                afterDialogHistory(() => onConfirm?.());
                return;
              }
              await onConfirm?.();
              if (closeOnConfirm) onClose();
            }}
          >
            {labels.confirm}
          </Button>
          <Button
            block
            variant="secondary"
            onClick={() => {
              onCancel?.();
              onClose();
            }}
          >
            {labels.cancel}
          </Button>
        </div>
      </div>
    </Modal>
  ));
}

/** Replacement for alert(): errors are a short toast, anything else a small dialog. */
export function showAlert({ title = "توجه", message }) {
  if (title === "خطا" && typeof message === "string") return showErrorToast(message);
  return modals.open({ title, children: message });
}

const pushedPages = new Set();

/**
 * A page's back arrow: a real back when the app pushed this page (or Back returned to one it pushed), so
 * the page under it comes back as it was and the phone's Back does not return here. A page opened from a
 * link or a reload has nothing of ours behind it: the arrow then replaces it with `fallback`.
 */
export function usePageBack(fallback = "/chats") {
  const navigate = useNavigate();
  const type = useNavigationType();
  const [pushed] = useState(() => {
    const page = typeof window === "undefined" ? "" : window.location.pathname;
    if (type === "PUSH") pushedPages.add(page);
    return type === "PUSH" || (type === "POP" && pushedPages.has(page));
  });
  return useCallback(() => {
    if (pushed) navigate(-1);
    else navigate(fallback, { replace: true });
  }, [navigate, pushed, fallback]);
}
